import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useGetMatchByIdQuery } from "../../../store/apis";
import { useDispatch, useSelector } from "react-redux";
import { addBet, removeBet } from "../../../store/betslip-slice";
import { toastService } from "../../notificationPage/ToastContainer";
import selectdropdownsvg from "../../../images/selectdropdownsvg.svg";
import DetailNavbar from "./DetailNavbar";

const MatchDetail = ({ matchId, setShowDetail, showDetail }) => {
  const [expandedMarkets, setExpandedMarkets] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Popular");
  const dispatch = useDispatch();
  const bets = useSelector((state) => state.betSlip.bets);

  const { data, isLoading, error } = useGetMatchByIdQuery(matchId);

  // Market group mapping
  const marketGroups = useMemo(
    () => ({
      Popular: [
        "Match Result",
        "Double Chance",
        "Both Teams to Score",
        "Total Goals",
        "Over/Under",
        "Correct Score",
        "Half Time/Full Time",
        "To Qualify",
        "To Win Either Half",
        "First Team to Score",
      ],
      Match: [
        "Match Result",
        "Double Chance",
        "Draw No Bet",
        "Half Time/Full Time",
        "Highest Scoring Half",
        "1st Half Result",
        "To Qualify",
      ],
      Team: [
        "Home Team to Score a Goal",
        "Away Team to Score a Goal",
        "Away Team Total Goals",
        "Home Team Total Goals",
        "Total Goals (3 Way)",
        "Home Team to Score 2 Goals in a Row",
        "Away Team to Score 2 Goals in a Row",
        "Home Team To Score First And Win The Match",
        "Away Team To Score First And Win The Match",
        "Home Team Win to Nil",
        "Home Team To Win From Behind",
        "Home Team to Win Both Halves",
        "Home Team To Win At Least One Half",
        "Away Team To Win At Least One Half",
        "Home Team to Score in Both Halves",
        "First Team to Score",
        "Last Team To Score",
      ],
      Goals: [
        "Total Goals",
        "Next Goal",
        "In Which Half First Goal Will Be Scored?",
        "Both Teams to Score",
        "Goals Odd/Even",
        "First Half Goals",
        "Second Half Goals",
        "Total Goals Number",
        "Away Team Goals Odd/Even",
        "Race To Goals",
      ],
      Draw: ["Draw In Both Halves", "Draw in Either Half"],
      Handicap: ["Handicap", "Asian Handicap", "European Handicap"],
      Player: [
        "First Goalscorer",
        "Anytime Goalscorer",
        "Last Goalscorer",
        "Player To Score 2 Or More Goals",
        "Player To Score 3 Or More Goals",
        "Player To Score In Anytime",
        "Any Player To Score At Least 2 Goals",
      ],
      Specials: ["Correct Score", "Scorecast", "Wincast"],
      All: [], // Will include all markets
    }),
    []
  );

  // Enhanced version that also handles partial matches and aliases
  const getMarketsByCategoryEnhanced = useCallback(
    (category, allMarkets) => {
      if (category === "All") return allMarkets;

      const groupMarkets = marketGroups[category] || [];
      return allMarkets.filter((market) => {
        // Exact match
        if (groupMarkets.includes(market.marketName)) return true;

        // Partial match (e.g., "Total Goals" matches "Total Goals - Over/Under")
        const marketNameLower = market.marketName.toLowerCase();
        return groupMarkets.some((groupMarket) =>
          marketNameLower.includes(groupMarket.toLowerCase())
        );
      });
    },
    [marketGroups]
  );
  // Helper function to get market key based on market name
  const getMarketKey = (marketName) => {
    const mapping = {
      "Match Result": "1X2",
      "Double Chance": "DoubleChance",
      "Both Teams to Score": "GG/Ng",
      "Draw No Bet": "Drawnobet",
      "Total Goals": "Over/Under",
      "First Goalscorer": "1stgoal",
      "Last Goalscorer": "Lastgoal",
    };

    return mapping[marketName] || marketName.replace(/\s+/g, "");
  };
  // Helper function to get display title based on market name
  const getMarketTitle = (marketName) => {
    const mapping = {
      "Match Result": "1X2",
      "Double Chance": "Double chance",
      "Both Teams to Score": "GG/NG",
      "Draw No Bet": "Draw no bet",
      "Total Goals": "Over/Under",
      "First Goalscorer": "1st goal",
      "Last Goalscorer": "Last goal",
    };

    return mapping[marketName] || marketName;
  };
  // Get filtered markets based on active category
  const filteredMarkets = useMemo(() => {
    if (!data?.events?.[0]) return [];

    const allMarkets = [
      // Include main odds as "Match Result" if available
      ...(data.events[0].odds
        ? [
            {
              marketName: "Match Result",
              marketOdds: data.events[0].odds,
              key: "1X2",
              title: "1X2",
            },
          ]
        : []),
      // Include additional markets with their keys and titles
      ...(data.events[0].additionalMarkets || []).map((market) => ({
        ...market,
        key: getMarketKey(market.marketName),
        title: getMarketTitle(market.marketName),
      })),
    ];

    return getMarketsByCategoryEnhanced(activeCategory, allMarkets);
  }, [data, activeCategory, getMarketsByCategoryEnhanced]);

  // Check if an odd is already in the betslip
  const isOddSelected = useCallback(
    (odd, market) => {
      if (!data || !data.events || !data.events[0]) return false;

      return bets.some(
        (bet) =>
          bet.match.id === matchId &&
          bet.odd.label === odd.label &&
          bet.market?.marketName === market.marketName
      );
    },
    [bets, matchId, data]
  );

  // Handle odd selection with single bet per match restriction
  const handleOddSelect = useCallback(
    (odd, market) => {
      if (!data || !data.events || !data.events[0]) return;

      const event = data.events[0];
      const match = {
        id: matchId,
        name: event.gameName,
        date: event.eventDate,
        market: market.marketName,
      };

      const betExists = isOddSelected(odd, market);
      const hasAnyBetForMatch = bets.some((bet) => bet.match.id === matchId);

      if (betExists) {
        dispatch(removeBet({ match, odd, market }));
        toastService.warning(`Removed: ${odd.label} from ${market.marketName}`);
      } else if (hasAnyBetForMatch) {
        const existingBets = bets.filter((bet) => bet.match.id === matchId);
        existingBets.forEach((bet) => {
          dispatch(
            removeBet({ match: bet.match, odd: bet.odd, market: bet.market })
          );
        });
        dispatch(addBet({ match, odd, market }));
        toastService.info(
          `Changed selection to: ${odd.label} in ${market.marketName}`
        );
      } else {
        dispatch(addBet({ match, odd, market }));
        toastService.success(`Added: ${odd.label} @ ${odd.value}`);
      }
    },
    [bets, data, dispatch, isOddSelected, matchId]
  );

  // Toggle market expansion
  const toggleMarketExpansion = useCallback((marketKey) => {
    setExpandedMarkets((prev) =>
      prev.includes(marketKey)
        ? prev.filter((m) => m !== marketKey)
        : [...prev, marketKey]
    );
  }, []);

  // Common render method for odds
  // Enhanced renderOdds function that adapts columns based on odds count
  const renderOdds = (market, layout = "auto") => {
    if (!market || !market.marketOdds) return null;

    const oddsCount = market.marketOdds.length;

    // Determine optimal layout based on odds count if set to auto
    let effectiveLayout = layout;
    if (layout === "auto") {
      if (oddsCount <= 3) {
        effectiveLayout = "horizontal";
      } else if (oddsCount > 6) {
        effectiveLayout = "vertical";
      } else {
        effectiveLayout = "grid";
      }
    }

    // Calculate grid columns based on odds count
    const getGridColumns = () => {
      if (oddsCount <= 3) return "grid-cols-" + oddsCount;
      if (oddsCount <= 6) return "grid-cols-3 md:grid-cols-3";
      if (oddsCount <= 9) return "grid-cols-2 md:grid-cols-3";
      return "grid-cols-2 md:grid-cols-4";
    };

    // Determine container class based on layout
    const containerClass = {
      horizontal:
        "flex flex-row justify-between p-3 s18:p-[15px_23px] w-full gap-2",
      vertical: "flex flex-col gap-2 py-3 px-3 s18:px-[23px]",
      grid: `grid ${getGridColumns()} gap-2 p-3 s18:p-[15px_23px]`,
    }[effectiveLayout];

    // Special case for odds with many options (like correct score)
    const isCorrectScore = market.marketName === "Correct Score";
    const correctScoreClass = isCorrectScore
      ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-3 s18:p-[15px_23px]"
      : "";

    return (
      <div className={isCorrectScore ? correctScoreClass : containerClass}>
        {market.marketOdds.map((odd, index) => (
          <OddItem
            key={index}
            odd={odd}
            market={market}
            selected={isOddSelected(odd, market)}
            layout={effectiveLayout}
          />
        ))}
      </div>
    );
  };
  // Common component for rendering an odd item
  // Enhanced OddItem component with responsive design
  const OddItem = ({ odd, market, selected, layout }) => {
    const isGGNG = market.marketName === "Both Teams to Score";
    const isGoalMarket = market.marketName.includes("Goalscorer");
    const isNoneOption = odd.label.toLowerCase() === "none";
    const isCornerMarket = market.marketName.includes("Corner");
    const isHandicapMarket = market.marketName.includes("Handicap");

    // Adjust width based on layout and market type
    const getWidthClass = () => {
      if (layout === "horizontal") return "flex-1";
      if (layout === "vertical") return "w-full";
      return "w-full"; // For grid layout
    };

    // Add special styling for handicap markets
    const handicapClass = isHandicapMarket
      ? "flex-col items-start pl-4"
      : "flex-col items-center";

    return (
      <div
        onClick={() => handleOddSelect(odd, market)}
        className={`cursor-pointer rounded-md ${getWidthClass()} py-3 md:py-2 s18:py-3 flex ${handicapClass} justify-center border transition-all duration-200 ${
          selected
            ? "bg-yellow border-yellow"
            : "bg-transparent border-doubleextralightwhite hover:border-yellow/50"
        }`}
      >
        {isHandicapMarket && (
          <div className="text-xs text-gray-300 mb-1">{odd.handicap || ""}</div>
        )}

        <div className="cursor-pointer w-full flex gap-[4px] items-center justify-center">
          <span className="text-white text-xs font-normal leading-normal md:leading-tight s18:leading-normal">
            {odd.label}
          </span>
          {isGGNG && (
            <span
              className={`ml-[2px] mb-[1px] w-0 h-0 ${
                odd.highlight
                  ? "border-b-[9px] border-b-[#11A306]"
                  : "border-t-[9px] border-t-[#ec3737]"
              } border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent`}
            />
          )}
          {(isGoalMarket || isCornerMarket) && !isNoneOption && (
            <span
              className={`ml-[2px] mb-[1px] w-0 h-0 ${
                odd.highlight
                  ? "border-b-[9px] border-b-[#11A306]"
                  : "border-t-[9px] border-t-[#ec3737]"
              } border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent`}
            />
          )}
        </div>
        <span className="text-white text-sm s18:text-base font-medium leading-normal md:leading-tight s18:leading-normal">
          {odd.value}
        </span>
      </div>
    );
  };

  // Auto-expand all markets on initial render
  useEffect(() => {
    if (filteredMarkets.length > 0) {
      setExpandedMarkets(filteredMarkets.map((market) => market.key));
    }
  }, [filteredMarkets]);

  if (isLoading)
    return <div className="p-6 text-center">Loading match data...</div>;
  if (error)
    return (
      <div className="p-6 text-center text-red-500">
        Error loading match data
      </div>
    );
  if (!data || !data.events || data.events.length === 0) {
    return <div className="p-6 text-center">No match data available</div>;
  }

  return (
    <>
      <DetailNavbar
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
      />

      <div className="bg-teal shadow-dashboardshadow sm:!rounded-[0_0_12px_12px]">
        {filteredMarkets.map((market) => {
          return (
            <div key={market.key} className="w-full relative">
              <div
                onClick={() => toggleMarketExpansion(market.key)}
                className="w-full relative flex justify-between items-center cursor-pointer max-sm:p-[10px_15px] p-[10px_23px] bg-forestGreen/50"
              >
                <span className="text-white max-sm:text-xs text-sm font-medium leading-[128%] uppercase">
                  {market.title}
                </span>
                <img src={selectdropdownsvg} alt="downarrow" className="w-4" />
              </div>
              {expandedMarkets.includes(market.key) &&
                renderOdds(market, "auto")}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default MatchDetail;
